import type { Db } from "./client";
import { getUserId } from "./client";
import {
  plantAttributeCreateSchema,
  plantAttributeUpdateSchema,
  type PlantAttributeCreateInput,
  type PlantAttributeUpdateInput,
} from "./schemas";
import { ValidationError, NotFoundError, throwIfPgError } from "./errors";
import type {
  PlantAttributeRow,
  PlantAttributeInsert,
} from "@garden/types/db";

export async function createAttribute(
  client: Db,
  input: PlantAttributeCreateInput,
): Promise<PlantAttributeRow> {
  const parsed = plantAttributeCreateSchema.safeParse(input);
  if (!parsed.success) {
    throw new ValidationError("invalid attribute input", parsed.error);
  }
  const userId = await getUserId(client);
  const { data, error } = await client
    .from("plant_attribute")
    .insert({ ...parsed.data, user_id: userId } as PlantAttributeInsert) // `as` cast sanctioned at Supabase boundary per spec §5
    .select("*")
    .single();
  throwIfPgError(error);
  return data as PlantAttributeRow; // `as` cast sanctioned at Supabase boundary per spec §5
}

export async function listAttributesForPlant(
  client: Db,
  plantId: string,
): Promise<PlantAttributeRow[]> {
  const { data, error } = await client
    .from("plant_attribute")
    .select("*")
    .eq("plant_id", plantId)
    .order("created_at", { ascending: true });
  throwIfPgError(error);
  return (data ?? []) as PlantAttributeRow[];
}

export async function updateAttribute(
  client: Db,
  id: string,
  patch: PlantAttributeUpdateInput,
): Promise<PlantAttributeRow> {
  const parsed = plantAttributeUpdateSchema.safeParse(patch);
  if (!parsed.success) {
    throw new ValidationError("invalid attribute patch", parsed.error);
  }
  const { data, error } = await client
    .from("plant_attribute")
    .update(parsed.data as Partial<Omit<PlantAttributeInsert, "user_id">>) // `as` cast sanctioned at Supabase boundary per spec §5
    .eq("id", id)
    .select("*")
    .maybeSingle();
  throwIfPgError(error);
  if (!data) throw new NotFoundError("plant_attribute", id);
  return data as PlantAttributeRow; // `as` cast sanctioned at Supabase boundary per spec §5
}

export async function deleteAttribute(client: Db, id: string): Promise<void> {
  const { error } = await client.from("plant_attribute").delete().eq("id", id);
  throwIfPgError(error);
}
